import React from 'react'

import {
  Alert,
  Box,
  Card,
  Divider,
  RadioButton,
  StatusLabel,
  Text,
  ToggleButton,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'
import { merge } from 'lodash'
import moment from 'moment'

import Callout from 'modules/core/components/Callout'
import DayPicker from 'modules/core/components/DayPicker'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import DialCodeSelector from 'modules/expenses/components/common/DialCodeSelector'
import type { WizardData } from 'modules/procurement/components/purchaseOrders/UpdateOrderModal/types'

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>
}

interface Props {
  selected?: boolean
  preferred?: boolean
  canCreateSingleUseCards: boolean
}

const BUDGET_FLEXIBILITY_OPTIONS: (0 | 5 | 10 | 20 | 30)[] = [0, 5, 10, 20, 30]

const VirtualCardOption: React.FC<
  Pick<WizardStepRenderParams<WizardData>, 'data' | 'onDataChanged'> & Props
> = ({ preferred, selected, canCreateSingleUseCards, data, onDataChanged }) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const handleSelected = () => {
    onDataChanged({
      ...data,
      preferredPaymentMethod: 'virtualcard',
    })
  }

  const setData = (partialData: RecursivePartial<typeof data>) =>
    onDataChanged({ ...merge(data, partialData) })

  return (
    <Card onClick={handleSelected}>
      {preferred && (
        <Card.Eyebrow>
          <StatusLabel type="info">
            {i18n.t(
              'procurement.purchase_orders.update_order_wizard.steps.payment_method.preferred_option'
            )}
          </StatusLabel>
        </Card.Eyebrow>
      )}
      <Card.Title
        action={
          <RadioButton
            value="card"
            checked={!!selected}
            onChange={(event) => event.target.value === 'card' && handleSelected()}
            disabled={!canCreateSingleUseCards}
          />
        }
      >
        {i18n.t(
          'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.title'
        )}
      </Card.Title>
      <Card.Description>
        {i18n.t(
          'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.description'
        )}
      </Card.Description>
      {!canCreateSingleUseCards ? (
        <Card.Content>
          <Callout
            type="warning"
            compact
            description={i18n.t(
              'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.warning'
            )}
          />
        </Card.Content>
      ) : (
        selected && (
          <Card.ExpandableContent
            expanded
            onExpandChange={(expanded) => expanded && handleSelected()}
          >
            <Box gap="s8">
              <Box gap="s16">
                <Box paddingY="s8">
                  <Text.Header2>
                    {i18n.t(
                      'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.title'
                    )}
                  </Text.Header2>
                </Box>

                <TextField
                  label={i18n.t(
                    'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.card_alias.title'
                  )}
                  description={i18n.t(
                    'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.card_alias.description'
                  )}
                  onChange={(value) => setData({ cardPaymentDetails: { cardAlias: value ?? '' } })}
                  value={data.cardPaymentDetails?.cardAlias ?? ''}
                  mandatory
                />

                <Text.Normal size="200" weight="medium" color="grey1000">
                  {i18n.t(
                    'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.budget_flexibility.title'
                  )}
                </Text.Normal>
                <Text.Normal size="200">
                  {i18n.t(
                    'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.budget_flexibility.description'
                  )}
                </Text.Normal>

                <Box gap="s16" flexDirection="row">
                  {BUDGET_FLEXIBILITY_OPTIONS.map((value) => (
                    <Box width="s80" key={value}>
                      <ToggleButton
                        label={value + '%'}
                        toggled={value === (data.cardPaymentDetails?.budgetFlexibility ?? 0)}
                        onClick={() =>
                          setData({
                            cardPaymentDetails: {
                              budgetFlexibility:
                                // eslint-disable-next-line no-type-assertion/no-type-assertion
                                value as (typeof BUDGET_FLEXIBILITY_OPTIONS)[number],
                            },
                          })
                        }
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box padding="s16">
                <Divider component="div" />
              </Box>

              <Box gap="s16">
                <Box>
                  <Text.Header2>
                    {i18n.t('software_management.initial_setup.wizard.steps.cardholder.title')}
                  </Text.Header2>
                </Box>

                <Text.Normal size="200" weight="medium" color="grey1000">
                  {i18n.t(
                    'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.cardholder.title'
                  )}
                </Text.Normal>
                <Text.Normal size="200">
                  {i18n.t(
                    'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.cardholder.description'
                  )}
                </Text.Normal>

                <Box flexDirection="row" gap="s8">
                  <TextField
                    label={i18n.t(
                      'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.first_name.title'
                    )}
                    onChange={(value) =>
                      setData({
                        cardPaymentDetails: { cardholderData: { firstName: value ?? '' } },
                      })
                    }
                    value={data.cardPaymentDetails?.cardholderData?.firstName ?? ''}
                    mandatory
                  />
                  <TextField
                    label={i18n.t(
                      'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.last_name.title'
                    )}
                    onChange={(value) =>
                      setData({
                        cardPaymentDetails: { cardholderData: { lastName: value } },
                      })
                    }
                    value={data.cardPaymentDetails?.cardholderData?.lastName ?? ''}
                    mandatory
                  />
                </Box>

                <DayPicker
                  label={i18n.t(
                    'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.birthdate.title'
                  )}
                  onChange={(value) =>
                    setData({
                      cardPaymentDetails: {
                        cardholderData: {
                          dateOfBirth: moment(convertDate(value, 'ui', 'backend_datetime')),
                        },
                      },
                    })
                  }
                  value={
                    data.cardPaymentDetails?.cardholderData?.dateOfBirth
                      ? convertDate(
                          data.cardPaymentDetails?.cardholderData?.dateOfBirth.toISOString(),
                          'backend_datetime',
                          'ui'
                        )
                      : ''
                  }
                  mandatory
                />

                <Box flexDirection="row" gap="s16">
                  <Box>
                    <DialCodeSelector
                      label={i18n.t(
                        'software_management.initial_setup.wizard.cardholder_form.country_code'
                      )}
                      onChange={(value) =>
                        setData({
                          cardPaymentDetails: {
                            cardholderData: {
                              phoneCountryCode: value ?? '',
                            },
                          },
                        })
                      }
                      value={data.cardPaymentDetails?.cardholderData?.phoneCountryCode}
                      locale={locale}
                      mandatory
                    />
                  </Box>

                  <Box flexGrow>
                    <TextField
                      label={i18n.t(
                        'software_management.initial_setup.wizard.cardholder_form.phone_number'
                      )}
                      onChange={(value) =>
                        setData({
                          cardPaymentDetails: { cardholderData: { phoneNumber: value ?? '' } },
                        })
                      }
                      mandatory
                      value={data.cardPaymentDetails?.cardholderData?.phoneNumber ?? ''}
                    />
                  </Box>
                </Box>
              </Box>

              <Box paddingTop="s24">
                <Alert color="info">
                  <Box gap="s8" flexDirection="row">
                    <Text.Normal>
                      {i18n.tView(
                        'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.info_card',
                        {
                          amount: (
                            <Text.Normal weight="medium">
                              {MoneyWithCurrency({
                                amount: data.cardPaymentDetails?.budgetFlexibility
                                  ? Math.round(
                                      data.purchaseOrderCost.cents *
                                        (1 + data.cardPaymentDetails.budgetFlexibility / 100)
                                    )
                                  : data.purchaseOrderCost.cents,
                                //TODO: Define what we want to do when the currency of the PO does not match the legal entity currency
                                currency: 'EUR',
                                locale,
                              })}
                            </Text.Normal>
                          ),

                          employee: (
                            <Text.Normal weight="medium">
                              {data.cardPaymentDetails?.cardholderData?.firstName}
                            </Text.Normal>
                          ),
                        }
                      )}
                    </Text.Normal>
                  </Box>
                </Alert>
              </Box>
            </Box>
          </Card.ExpandableContent>
        )
      )}
    </Card>
  )
}

export default VirtualCardOption
